import { useCallback, useEffect } from "react";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { MenuItem, Stack } from "@mui/material";
import { useMobileDrawer } from "@src/features/mobileAnalytics/shared/hooks/useMobileDrawer";
import { useDispatch, useSelector } from "@src/store";
import {
  getExperienceAnalyticsFilters,
  refreshData,
  updateDashboardFilter,
} from "@src/store/slices/dashboardFilters";
import { selectedVariations } from "@src/store/slices/variations";
import { useQueryClient } from "@tanstack/react-query";

type LocalFilters = {
  compareGroupA: string;
  compareGroupB: string;
};

export const CompareGroupsDrawerContent = () => {
  const dispatch = useDispatch();
  const drawer = useMobileDrawer();
  const queryClient = useQueryClient();
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const variations = useSelector(selectedVariations);

  // Initialize local state from Redux
  const initializeLocalFilters = useCallback(
    (): LocalFilters => ({
      compareGroupA: dashboardFilters.compareGroupA ?? "",
      compareGroupB: dashboardFilters.compareGroupB ?? "",
    }),
    [dashboardFilters.compareGroupA, dashboardFilters.compareGroupB]
  );

  const [localFilters, setLocalFilters] = useImmer(initializeLocalFilters());

  // Sync with Redux when drawer opens
  useEffect(() => {
    setLocalFilters(initializeLocalFilters());
  }, [
    dashboardFilters.compareGroupA,
    dashboardFilters.compareGroupB,
    initializeLocalFilters,
    setLocalFilters,
  ]);

  // Handle group selection with swap logic
  const handleGroupChange = (isGroupA: boolean, selectedValue: string) => {
    setLocalFilters((draft) => {
      if (isGroupA) {
        if (draft.compareGroupB === selectedValue) {
          // Swap: A takes new value, B takes A's old value
          const oldGroupA = draft.compareGroupA;
          draft.compareGroupA = selectedValue;
          draft.compareGroupB = oldGroupA;
        } else {
          // Normal selection
          draft.compareGroupA = selectedValue;
        }
      } else {
        if (draft.compareGroupA === selectedValue) {
          // Swap: B takes new value, A takes B's old value
          const oldGroupB = draft.compareGroupB;
          draft.compareGroupB = selectedValue;
          draft.compareGroupA = oldGroupB;
        } else {
          // Normal selection
          draft.compareGroupB = selectedValue;
        }
      }
    });
  };

  // Refresh dashboard data after filter update
  const refreshDashboardData = () => {
    dispatch(refreshData());

    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.some(
          (key) =>
            typeof key === "object" &&
            JSON.stringify(key).includes("dashboards")
        ),
    });
  };

  // Apply changes to Redux and close drawer
  const handleApply = () => {
    dispatch(
      updateDashboardFilter({
        key: "compareGroupA",
        value: localFilters.compareGroupA,
      })
    );
    dispatch(
      updateDashboardFilter({
        key: "compareGroupB",
        value: localFilters.compareGroupB,
      })
    );
    refreshDashboardData();
    drawer.closeDrawer();
  };

  return (
    <Stack sx={{ p: 2 }} spacing={3}>
      <Stack sx={{ minHeight: "30svh", flex: 1 }}>
        {/* Group A Select */}
        <IgSelect
          label="Group A"
          value={localFilters.compareGroupA}
          onChange={(e) => handleGroupChange(true, e.target.value)}
          fullWidth
          size="small"
        >
          {variations.map((variation) => (
            <MenuItem key={variation.id} value={variation.id}>
              {variation.name}
            </MenuItem>
          ))}
        </IgSelect>

        {/* VS Divider */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <IgDivider sx={{ flex: 1 }} />
          <IgTypography
            sx={{
              fontWeight: 600,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            vs
          </IgTypography>
          <IgDivider sx={{ flex: 1 }} />
        </Stack>

        {/* Group B Select */}
        <IgSelect
          label="Group B"
          value={localFilters.compareGroupB}
          onChange={(e) => handleGroupChange(false, e.target.value)}
          fullWidth
          size="small"
        >
          {variations.map((variation) => (
            <MenuItem key={variation.id} value={variation.id}>
              {variation.name}
            </MenuItem>
          ))}
        </IgSelect>
      </Stack>

      {/* Apply Button */}
      <IgButton
        variant="contained"
        fullWidth
        onClick={handleApply}
        sx={{ position: "sticky", bottom: 0, justifySelf: "flex-end" }}
      >
        Apply
      </IgButton>
    </Stack>
  );
};
